export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

const demoMode = import.meta.env.VITE_DEMO_MODE === "true";

export async function api<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  if (demoMode) {
    await new Promise((resolve) => window.setTimeout(resolve, 120));
    return demoResponse(path, options) as T;
  }

  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData)) {
    headers.set("content-type", "application/json");
  }
  headers.set("accept", "application/json");

  const response = await fetch(path, {
    ...options,
    headers,
    credentials: "include",
  });
  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json")
    ? ((await response.json()) as { message?: string })
    : null;
  if (!response.ok) {
    throw new ApiError(
      payload?.message ?? `Ошибка запроса (${response.status})`,
      response.status,
    );
  }
  return payload as T;
}

export async function download(
  path: string,
  body: Record<string, unknown>,
): Promise<void> {
  if (demoMode) {
    const blob = new Blob(
      [
        "Apple Bee CRM demo export\n",
        `Requested payload: ${JSON.stringify(body, null, 2)}\n`,
        "The real system generates a validated XLSX file on the server.",
      ],
      { type: "text/plain;charset=utf-8" },
    );
    saveBlob(blob, "apple-bee-demo-export.txt");
    return;
  }

  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      accept:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      message?: string;
    } | null;
    throw new ApiError(payload?.message ?? "Не удалось создать файл", response.status);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") ?? "";
  const fileName =
    disposition.match(/filename="([^"]+)"/u)?.[1] ?? "apple-bee-export.xlsx";
  saveBlob(blob, fileName);
}

function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Неизвестная ошибка";
}

const demoUser = {
  id: "11111111-1111-4111-8111-111111111111",
  email: "demo@example.kz",
  displayName: "Демо Руководитель",
  roles: [
    "CEO",
    "IT_ADMIN",
    "OPERATIONS",
    "ACCOUNTANT",
    "LOGISTICS",
    "STOREKEEPER",
  ],
  isActive: true,
  mfaEnabled: true,
  lastLoginAt: new Date().toISOString(),
};

const demoInventory = [
  {
    balanceId: "21111111-1111-4111-8111-111111111111",
    productId: "31111111-1111-4111-8111-111111111111",
    productName: "Яблоко Гала",
    warehouseId: "41111111-1111-4111-8111-111111111111",
    warehouseName: "Холодильный склад №1",
    lotId: "51111111-1111-4111-8111-111111111111",
    lotCode: "GAL-2026-0718-A",
    quantityKg: 12_480,
    reservedKg: 2_200,
    availableKg: 10_280,
    updatedAt: new Date(Date.now() - 22 * 60_000).toISOString(),
  },
  {
    balanceId: "22222222-2222-4222-8222-222222222222",
    productId: "32222222-2222-4222-8222-222222222222",
    productName: "Яблоко Голден",
    warehouseId: "41111111-1111-4111-8111-111111111111",
    warehouseName: "Холодильный склад №1",
    lotId: "52222222-2222-4222-8222-222222222222",
    lotCode: "GLD-2026-0721-B",
    quantityKg: 8_760,
    reservedKg: 0,
    availableKg: 8_760,
    updatedAt: new Date(Date.now() - 97 * 60_000).toISOString(),
  },
  {
    balanceId: "23333333-3333-4333-8333-333333333333",
    productId: "33333333-3333-4333-8333-333333333333",
    productName: "Яблоко Айдаред",
    warehouseId: "42222222-2222-4222-8222-222222222222",
    warehouseName: "Склад готовой продукции",
    lotId: "53333333-3333-4333-8333-333333333333",
    lotCode: "AID-2026-0714-C",
    quantityKg: 680,
    reservedKg: 400,
    availableKg: 280,
    updatedAt: new Date(Date.now() - 3.5 * 3_600_000).toISOString(),
  },
];

function demoResponse(path: string, options: RequestInit): unknown {
  const method = options.method ?? "GET";
  if (path === "/api/auth/me") return { user: demoUser };
  if (path === "/api/auth/logout") return { status: "SIGNED_OUT" };
  if (path === "/api/dashboard/summary") {
    return {
      summary: {
        totalStockKg: 21_920,
        activeLots: 3,
        pendingShipments: 2,
        shippedToday: 1_850,
        lowStockItems: [
          {
            productName: "Яблоко Айдаред",
            warehouseName: "Склад готовой продукции",
            availableKg: 280,
          },
        ],
        recentMovements: demoInventory,
      },
    };
  }
  if (path === "/api/inventory") return { inventory: demoInventory };
  if (path.startsWith("/api/inventory/movements") && method === "GET") {
    return {
      movements: [
        {
          id: "61111111-1111-4111-8111-111111111111",
          direction: "IN",
          quantityKg: 4_200,
          reason: "Приёмка после сортировки",
          productName: "Яблоко Гала",
          warehouseName: "Холодильный склад №1",
          lotCode: "GAL-2026-0718-A",
          createdByName: "Демо Оператор",
          createdAt: new Date(Date.now() - 22 * 60_000).toISOString(),
          reversalOf: null,
        },
        {
          id: "62222222-2222-4222-8222-222222222222",
          direction: "OUT",
          quantityKg: 1_850,
          reason: "Отгрузка REQ-2026-001014",
          productName: "Яблоко Голден",
          warehouseName: "Холодильный склад №1",
          lotCode: "GLD-2026-0721-B",
          createdByName: "Демо Логист",
          createdAt: new Date(Date.now() - 2.2 * 3_600_000).toISOString(),
          reversalOf: null,
        },
      ],
    };
  }
  if (path === "/api/reference/products") {
    return {
      products: [
        {
          id: "31111111-1111-4111-8111-111111111111",
          name: "Яблоко Гала",
          sku: "APL-GALA",
        },
        {
          id: "32222222-2222-4222-8222-222222222222",
          name: "Яблоко Голден",
          sku: "APL-GLD",
        },
      ],
    };
  }
  if (path === "/api/reference/warehouses") {
    return {
      warehouses: [
        {
          id: "41111111-1111-4111-8111-111111111111",
          name: "Холодильный склад №1",
          code: "COLD-1",
        },
        {
          id: "42222222-2222-4222-8222-222222222222",
          name: "Склад готовой продукции",
          code: "FINISHED",
        },
      ],
    };
  }
  if (path === "/api/reference/customers") {
    return {
      customers: [
        {
          id: "71111111-1111-4111-8111-111111111111",
          name: "Демо-клиент",
        },
      ],
    };
  }
  if (path === "/api/shipments") {
    return {
      shipments: [
        {
          id: "81111111-1111-4111-8111-111111111111",
          number: "REQ-2026-001015",
          customerName: "Демо-клиент",
          requestedDate: new Date(Date.now() + 86_400_000)
            .toISOString()
            .slice(0, 10),
          status: "PENDING_APPROVAL",
          notes: "Демонстрационная заявка",
          createdByName: "Демо Оператор",
          createdAt: new Date(Date.now() - 55 * 60_000).toISOString(),
          lines: [
            {
              id: "91111111-1111-4111-8111-111111111111",
              productId: demoInventory[0]?.productId,
              productName: demoInventory[0]?.productName,
              warehouseId: demoInventory[0]?.warehouseId,
              warehouseName: demoInventory[0]?.warehouseName,
              lotId: demoInventory[0]?.lotId,
              lotCode: demoInventory[0]?.lotCode,
              quantityKg: 2_200,
            },
          ],
        },
        {
          id: "82222222-2222-4222-8222-222222222222",
          number: "REQ-2026-001014",
          customerName: "Демо-клиент",
          requestedDate: new Date().toISOString().slice(0, 10),
          status: "SHIPPED",
          notes: null,
          createdByName: "Демо Логист",
          createdAt: new Date(Date.now() - 6 * 3_600_000).toISOString(),
          lines: [
            {
              id: "92222222-2222-4222-8222-222222222222",
              productId: demoInventory[1]?.productId,
              productName: demoInventory[1]?.productName,
              warehouseId: demoInventory[1]?.warehouseId,
              warehouseName: demoInventory[1]?.warehouseName,
              lotId: demoInventory[1]?.lotId,
              lotCode: demoInventory[1]?.lotCode,
              quantityKg: 1_850,
            },
          ],
        },
      ],
    };
  }
  if (path === "/api/admin/users") {
    return {
      users: [
        demoUser,
        {
          ...demoUser,
          id: "12222222-2222-4222-8222-222222222222",
          email: "operator@example.kz",
          displayName: "Демо Оператор",
          roles: ["OPERATIONS"],
          lastLoginAt: new Date(Date.now() - 2 * 3_600_000).toISOString(),
        },
      ],
    };
  }
  if (path === "/api/admin/invitations") return { invitations: [] };
  if (path.includes("/api/admin/users/") && path.endsWith("/password-reset")) {
    return {
      status: "PASSWORD_RESET_CREATED",
      recovery: {
        recoveryUrl: "https://demo.invalid/?recover=demo-one-time-token",
        expiresAt: new Date(Date.now() + 30 * 60_000).toISOString(),
      },
    };
  }
  if (path === "/api/accounting/exports") {
    return {
      exports: [
        {
          id: "a1111111-1111-4111-8111-111111111111",
          number: "EXP-2026-001006",
          type: "STOCK_MOVEMENTS",
          periodFrom: "2026-07-01",
          periodTo: "2026-07-29",
          fileName: "EXP-2026-001006_2026-07-01_2026-07-29.xlsx",
          sha256: "049bd58350de7a6acb068b9a9dc55be46f763b8fa1b5ce68783893cc7b19d0d1",
          status: "CREATED",
          oneCDocumentNumbers: [],
          createdByName: "Демо Бухгалтер",
          createdAt: new Date(Date.now() - 26 * 3_600_000).toISOString(),
        },
      ],
    };
  }
  if (path === "/api/integrations/google-sheets/status") {
    return {
      enabled: false,
      configured: false,
      mode: "AGGREGATED_ONE_WAY",
      containsPersonalData: false,
    };
  }
  if (path === "/api/legacy-references/") {
    return {
      records: [
        {
          id: "d1111111-1111-4111-8111-111111111111",
          dataset: "gardens",
          sourceKey: "demo-garden-1",
          record: { name: "Демонстрационный сад", code: "G-01", area: 12.5 },
          verificationStatus: "UNVERIFIED",
          reviewedBy: null,
          reviewerName: null,
          reviewedAt: null,
          reviewNote: null,
          createdAt: new Date(Date.now() - 86_400_000).toISOString(),
        },
        {
          id: "d2222222-2222-4222-8222-222222222222",
          dataset: "boxtypes",
          sourceKey: "demo-box-1",
          record: { name: "Демонстрационная коробка", weightKg: 12 },
          verificationStatus: "APPROVED",
          reviewedBy: demoUser.id,
          reviewerName: demoUser.displayName,
          reviewedAt: new Date(Date.now() - 3_600_000).toISOString(),
          reviewNote: "Проверено по внутреннему справочнику",
          createdAt: new Date(Date.now() - 86_400_000).toISOString(),
        },
      ],
      summary: {
        total: 2,
        unverified: 1,
        approved: 1,
        rejected: 0,
        datasets: [
          { dataset: "gardens", total: 1, unverified: 1, approved: 0, rejected: 0 },
          { dataset: "boxtypes", total: 1, unverified: 0, approved: 1, rejected: 0 },
        ],
      },
    };
  }
  if (path.startsWith("/api/audit")) {
    return {
      events: [
        {
          id: "b1111111-1111-4111-8111-111111111111",
          occurredAt: new Date(Date.now() - 22 * 60_000).toISOString(),
          actorName: "Демо Оператор",
          actorEmail: "operator@example.kz",
          action: "STOCK_MOVEMENT_CREATED",
          entityType: "stock_movement",
          entityId: "61111111-1111-4111-8111-111111111111",
          ipAddress: "10.0.0.24",
          details: { direction: "IN", quantityKg: 4200 },
        },
        {
          id: "b2222222-2222-4222-8222-222222222222",
          occurredAt: new Date(Date.now() - 55 * 60_000).toISOString(),
          actorName: "Демо Руководитель",
          actorEmail: "demo@example.kz",
          action: "AUTH_LOGIN_SUCCEEDED",
          entityType: "session",
          entityId: "c1111111-1111-4111-8111-111111111111",
          ipAddress: "10.0.0.8",
          details: {},
        },
      ],
    };
  }
  if (method === "POST") return { status: "DEMO_OK" };
  throw new ApiError(`Demo response is not defined for ${path}`, 404);
}
